import React, { useEffect, useRef, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { stackedBarPairedPalette } from "../colors";
import D3StackedBar from "./D3StackedBar";
import { appService } from "../../App/app.service";
import {
  getDefaultSalesType,
  getDefaultSelectedFiscalQuarters,
} from "../../util/customFunctions";
import { getNPriorQuarters } from "../../util/fiscalQuarterFunctions";
import { fetchFirstLevelTeamNames, fetchDefaultTeamFilter } from "../rccUtils";
import RccCard from "../RccCard";

/**
 * Displays trend of lost reasons over quarters.
 * Inputs: Redux state (closedLostOppData, closedLostOppFilters, company, user).
 * Outputs: Stacked bar chart of ACV by lost reason.
 */
function LostReasonTrend({ title, redirectToDashboard }) {
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [timeKey, setTimeKey] = useState(null);
  const [firstLevelTeamNames, setFirstLevelTeamNames] = useState([]);
  const [defaultTeamFilter, setDefaultTeamFilter] = useState([]);
  const [isFirstLevelTeamsLoaded, setIsFirstLevelTeamsLoaded] = useState(false);
  const [isDefaultTeamLoaded, setIsDefaultTeamLoaded] = useState(false);

  useEffect(() => {
    fetchFirstLevelTeamNames().then((names) => {
      setFirstLevelTeamNames(names);
      setIsFirstLevelTeamsLoaded(true);
    });
    fetchDefaultTeamFilter()
      .then((teams) => {
        setDefaultTeamFilter(teams);
        setIsDefaultTeamLoaded(true);
      })
      .catch((e) => {
        console.error("Error fetching default team filter:", e);
        setIsDefaultTeamLoaded(true);
      });
  }, []);

  const getTimeKeyFromFiltersResponse = (filtersResp) => {
    const message = filtersResp?.message;
    if (!Array.isArray(message)) return null;
    const meta = message.find(
      (item) =>
        item &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        typeof item.defaultTimeFrame === "string"
    );
    return meta?.defaultTimeFrame || null;
  };

  const getFallbackTimeValues = (message, key, count = 4) => {
    if (!Array.isArray(message) || !key) return [];
    const values = message
      .filter((row) => row && typeof row === "object" && row[key] != null)
      .map((row) => row[key])
      .filter(Boolean);
    const uniqSorted = Array.from(new Set(values)).sort();
    return uniqSorted.slice(Math.max(0, uniqSorted.length - count));
  };

  const parseQuarterKey = (value) => {
    // Supports values like "2025-Q1" (closed_fiscal_quarter).
    // Returns sortable number YYYYQ; falls back to lexical sort if not parseable.
    if (typeof value !== "string") return null;
    const m = value.match(/^(\d{4})-Q([1-4])$/);
    if (!m) return null;
    const year = Number(m[1]);
    const q = Number(m[2]);
    if (!Number.isFinite(year) || !Number.isFinite(q)) return null;
    return year * 10 + q;
  };

  const parseYearMonthKey = (value) => {
    // Supports values like "2025-01" / "2025-1" (Closed_Year_Month).
    // Returns sortable number YYYYMM; falls back to lexical sort if not parseable.
    if (typeof value !== "string") return null;
    const m = value.match(/^(\d{4})-(\d{1,2})$/);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
    return year * 100 + month;
  };

  const getDefaultPlusPriorQuarters = (message, quarterKey, backCount = 3) => {
    if (!Array.isArray(message) || !quarterKey) return [];

    const quarterValues = message
      .filter(
        (row) => row && typeof row === "object" && row[quarterKey] != null
      )
      .map((row) => row[quarterKey])
      .filter(Boolean);

    // Unique by value, sorted by parsed YYYY-Qn when possible.
    const uniq = Array.from(new Set(quarterValues));
    const sorted = uniq
      .map((v) => ({ v, k: parseQuarterKey(v) }))
      .sort((a, b) => {
        if (a.k != null && b.k != null) return a.k - b.k;
        return String(a.v).localeCompare(String(b.v));
      })
      .map((x) => x.v);

    // Determine the default quarter from selected_by_default.
    const defaultSelected = message
      .filter(
        (row) =>
          row &&
          typeof row === "object" &&
          row.selected_by_default === "Y" &&
          row[quarterKey] != null
      )
      .map((row) => row[quarterKey])
      .filter(Boolean);

    const anchor = defaultSelected[defaultSelected.length - 1] || null; // latest default if multiple
    const anchorIdx = anchor ? sorted.indexOf(anchor) : -1;
    const endIdx = anchorIdx >= 0 ? anchorIdx : sorted.length - 1;
    if (endIdx < 0) return [];

    const startIdx = Math.max(0, endIdx - backCount);
    return sorted.slice(startIdx, endIdx + 1);
  };

  const getDefaultPlusPriorMonths = (message, monthKey, backCount = 3) => {
    if (!Array.isArray(message) || !monthKey) return [];

    const monthValues = message
      .filter((row) => row && typeof row === "object" && row[monthKey] != null)
      .map((row) => row[monthKey])
      .filter(Boolean);

    // Unique by value, sorted by parsed YYYYMM when possible.
    const uniq = Array.from(new Set(monthValues));
    const sorted = uniq
      .map((v) => ({ v, k: parseYearMonthKey(v) }))
      .sort((a, b) => {
        if (a.k != null && b.k != null) return a.k - b.k;
        return String(a.v).localeCompare(String(b.v));
      })
      .map((x) => x.v);

    // Determine the default month from selected_by_default.
    const defaultSelected = message
      .filter(
        (row) =>
          row &&
          typeof row === "object" &&
          row.selected_by_default === "Y" &&
          row[monthKey] != null
      )
      .map((row) => row[monthKey])
      .filter(Boolean);

    const anchor = defaultSelected[defaultSelected.length - 1] || null; // latest default if multiple
    const anchorIdx = anchor ? sorted.indexOf(anchor) : -1;
    const endIdx = anchorIdx >= 0 ? anchorIdx : sorted.length - 1;
    if (endIdx < 0) return [];

    const startIdx = Math.max(0, endIdx - backCount);
    return sorted.slice(startIdx, endIdx + 1);
  };

  // Redux State
  const company = useSelector((state) => state.app.company);
  const user = useSelector((state) => state.app.user || {});
  const closedLostOppFilters = useSelector(
    (state) => state.app.closedLostOppFilters || ""
  );
  // This array contains: [FunnelData, TableData, StageData, LostReasonObject]
  const closedLostOppData = useSelector(
    (state) => state.app.closedLostOppData || ""
  );

  // Helper to check if data is actually loaded
  const isDataLoaded =
    Array.isArray(closedLostOppData) && closedLostOppData.length > 3;
  const effectiveTimeKey = timeKey || "closed_fiscal_quarter";
  const isLoading =
    (!isDataLoaded || closedLostOppFilters === "" || !isFirstLevelTeamsLoaded || !isDefaultTeamLoaded) &&
    user.currentFiscalQuarter;

  // --- Fetch Logic ---
  useEffect(() => {
    if (fetchingRef.current) return;

    // If we already have data and have applied the inferred timeframe, do nothing.
    if (isDataLoaded && appliedFilters.length > 0 && timeKey) return;

    if (
      (closedLostOppFilters === "" || closedLostOppData === "") &&
      isFirstLevelTeamsLoaded &&
      isDefaultTeamLoaded
    ) {
      fetchingRef.current = true;

      // Fetch Filters
      dispatch({ type: "get_closed_lost_opp_analysis_filters_request" });
      appService.getClosedLostOppAnalysisFilters().then(
        (resp) => {
          dispatch({
            type: "get_closed_lost_opp_analysis_filters_success",
            json: resp,
          });

          // Build Filters
          const inferredTimeKey =
            getTimeKeyFromFiltersResponse(resp) || "closed_fiscal_quarter";
          setTimeKey(inferredTimeKey);

          let defaultTimeValues = [];

          if (inferredTimeKey === "closed_fiscal_quarter") {
            // Quarter-based customers: keep the previous behavior (current + 3 prior).
            // This avoids relying on backend `selected_by_default`, which may only mark 1 quarter.
            if (user.currentFiscalQuarter) {
              const currentQuarter = user.currentFiscalQuarter;
              const priorQuarters = getNPriorQuarters(
                currentQuarter,
                3
              ).reverse();
              defaultTimeValues = [...priorQuarters, currentQuarter];
            } else {
              // If user current quarter is unavailable, derive "default + 3 prior" from filter payload.
              defaultTimeValues = getDefaultPlusPriorQuarters(
                resp.message,
                inferredTimeKey,
                3
              );
            }
          } else {
            const months = resp.message
              .filter(
                (row) =>
                  row && typeof row === "object" && row[inferredTimeKey] != null
              )
              .map((row) => row[inferredTimeKey])
              .filter(Boolean);
            const uniq = Array.from(new Set(months));
            const sorted = uniq
              .map((v) => ({ v, k: parseYearMonthKey(v) }))
              .sort((a, b) => {
                if (a.k != null && b.k != null) return a.k - b.k;
                return String(a.v).localeCompare(String(b.v));
              })
              .map((x) => x.v);
            const now = new Date();
            const currentMonthLabel = `${now.getFullYear()}-${String(
              now.getMonth() + 1
            ).padStart(2, "0")}`;
            const idx = sorted.indexOf(currentMonthLabel);
            if (idx >= 0) {
              const startIdx = Math.max(0, idx - 3);
              defaultTimeValues = sorted.slice(startIdx, idx + 1);
            } else {
              defaultTimeValues = getDefaultPlusPriorMonths(
                resp.message,
                inferredTimeKey,
                3
              );
            }
          }

          // Final fallback: take last N labels available in filter payload.
          if (defaultTimeValues.length === 0) {
            defaultTimeValues = getFallbackTimeValues(
              resp.message,
              inferredTimeKey,
              inferredTimeKey === "closed_fiscal_quarter" ? 4 : 4
            );
          }

          const defaultOppType = getDefaultSalesType(
            resp.message.filter((i) => i.type === "Type"),
            "Type"
          );
          const defaultSolutionCat = getDefaultSalesType(
            resp.message.filter((i) => i.type === "Solution_Cat"),
            "Solution_Cat"
          );
          const earliestLostStage = resp.message
            .filter(
              (item) =>
                item.type === "Earliest_Lost_Stage" &&
                item.selected_by_default === "Y"
            )
            .map((i) => i.stage_name);

          let filters = [
            {
              name: inferredTimeKey,
              value: defaultTimeValues,
              string: true,
            },
          ];

          if (earliestLostStage.length >= 1) {
            filters.push({
              name: "Earliest_Lost_Stage",
              value: earliestLostStage,
              string: true,
            });
          }

          if (company === "C0008") {
            const salesFilters = getDefaultSalesType(
              resp.message.filter((s) => s.type === "cross_sell1__c"),
              "cross_sell1__c"
            );
            const dealsFilters = getDefaultSalesType(
              resp.message.filter((s) => s.type === "Type"),
              "Type"
            );
            if (salesFilters[0] !== "All")
              filters.push({ name: "cross_sell1__c", value: salesFilters });
            if (dealsFilters[0] !== "All")
              filters.push({ name: "Type", value: dealsFilters });
          }

          if (defaultOppType[0] !== "All")
            filters.push({ name: "Type", value: defaultOppType });
          if (defaultSolutionCat[0] !== "All")
            filters.push({ name: "Solution_Cat", value: defaultSolutionCat });

          // Prioritize first-level teams, fallback to default team filter if not available
          const teamsToFilter =
            Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0
              ? firstLevelTeamNames
              : (Array.isArray(defaultTeamFilter) && defaultTeamFilter.length > 0
                  ? defaultTeamFilter
                  : []);

          if (Array.isArray(teamsToFilter) && teamsToFilter.length > 0) {
            filters.push({ name: "team", value: teamsToFilter });
          }

          filters = filters.filter(
            (f) =>
              (!Array.isArray(f.value) && f.value !== "All") ||
              !f.value.includes("All")
          );

          setAppliedFilters(filters);
          dispatch({ type: "current_selected_filters", filters });

          // Fetch Data
          dispatch({ type: "get_closed_lost_opp_analysis_data_request" });
          appService
            .getClosedLostOppAnalysisData(filters, inferredTimeKey)
            .then(
              (dataResp) => {
                // Add OppNo counter to table data (index 1)
                let counter = 0;
                let temp = dataResp.message[1].map((i) => ({
                  ...i,
                  OppNo: ++counter,
                }));

                const processedData = {
                  ...dataResp,
                  message: [
                    dataResp.message[0],
                    temp,
                    dataResp.message[2],
                    dataResp.message[3],
                  ],
                };

                fetchingRef.current = false;
                dispatch({
                  type: "get_closed_lost_opp_analysis_data_success",
                  json: processedData,
                });
              },
              (error) => {
                fetchingRef.current = false;
                dispatch({
                  type: "get_closed_lost_opp_analysis_failure",
                  error:
                    typeof error === "object" ? "Something went wrong" : error,
                });
              }
            );
        },
        (error) => {
          fetchingRef.current = false;
          dispatch({
            type: "get_closed_lost_opp_analysis_filters_failure",
            error: typeof error === "object" ? "Something went wrong" : error,
          });
        }
      );
    }
  }, [
    company,
    dispatch,
    closedLostOppFilters,
    closedLostOppData,
    user,
    isDataLoaded,
    appliedFilters.length,
    timeKey,
    isFirstLevelTeamsLoaded,
    isDefaultTeamLoaded,
    firstLevelTeamNames,
    defaultTeamFilter,
  ]);

  // --- Data Transformation ---
  const { chartData, derivedCategories } = useMemo(() => {
    // Safety check
    if (!isDataLoaded) return { chartData: [], derivedCategories: [] };

    // The data you shared is the 4th element (index 3)
    // Structure: { type: "lostReason", data: [ { count, acv, lostReason, quarter, year }, ... ] }
    const lostReasonObject = closedLostOppData[3];

    if (!lostReasonObject || !Array.isArray(lostReasonObject.data)) {
      return { chartData: [], derivedCategories: [] };
    }

    const rawData = lostReasonObject.data;

    // We need to group by Quarter, using 'lostReason' as keys and 'acv' as values.
    // IMPORTANT: Filter out rows where quarter="Total" or lostReason="Total"

    const quartersSet = new Set();
    const groupedData = {};
    // Total ACV per reason across all quarters (used to select Top 5)
    const totalByReason = new Map();

    rawData.forEach((item) => {
      // 1. FILTER: Exclude Totals
      if (item.quarter === "Total" || item.lostReason === "Total") return;

      const q = item.quarter;
      const r = item.lostReason;
      const val = Number(item.acv) || 0;

      // 2. Aggregate
      quartersSet.add(q);
      totalByReason.set(r, (totalByReason.get(r) || 0) + val);

      if (!groupedData[q]) {
        groupedData[q] = { xAxisLabel: q };
      }

      // Handle potential duplicate entries by summing
      groupedData[q][r] = (groupedData[q][r] || 0) + val;
    });

    // 3. Prepare Categories and Chart Data
    const categories = Array.from(totalByReason.entries())
      .sort((a, b) => b[1] - a[1]) // highest total ACV first
      .slice(0, 5)
      .map(([reason]) => reason);

    // Build rows using ONLY top-5 categories (drop the rest)
    const transformedData = Object.values(groupedData).map((row) => {
      const out = { xAxisLabel: row.xAxisLabel };
      categories.forEach((cat) => {
        out[cat] = row[cat] || 0;
      });
      return out;
    });

    // 4. Sort by Quarter (e.g. 2025-Q1 before 2025-Q2)
    transformedData.sort((a, b) => a.xAxisLabel.localeCompare(b.xAxisLabel));

    return { chartData: transformedData, derivedCategories: categories };
  }, [closedLostOppData, isDataLoaded]);

  const handleZoom = () => {
    // Add logic to open modal
    console.log("Zooming card...");
  };

  return (
    <RccCard
      title={title || "Closed Lost Opportunities by Reason"}
      isLoading={isLoading}
      redirectToDashboard={redirectToDashboard || "Closed Lost Analysis"}
      onZoomClick={handleZoom}
      appliedFilters={appliedFilters}
      focusVariant={"current"}
    >
      <D3StackedBar
        data={chartData}
        categories={derivedCategories}
        colorPalette={stackedBarPairedPalette}
        currencySymbol={user?.Currency}
        options={{
          showLabels: true,
          showLegend: true,
        }}
      />
    </RccCard>
  );
}

export default LostReasonTrend;
